/**
 * The search token algebra + highlight renderer shared by the PowerBar (which
 * owns the chip input UI) and the tables. One filter expression — term chips
 * joined by OR, evaluated left-to-right, with the live input text acting as one
 * more trailing term (AND while empty, OR once it has text) — no user-facing
 * operator toggles.
 *
 * One term kind: `text` (exact string semantics — filters rows, feeds the
 * document find engine, and filters the document's ◀▶ source track). Navigation
 * to a resolved article lives in the source track (◀▶ + the picker), never as a
 * chip — so there is no article/identity chip. Ported from the regulatory-cms
 * studio (components/TableSearch.tsx).
 */
export type FilterOp = "and" | "or"
export type FilterTerm = { kind: "text"; q: string }
export interface FilterToken {
  term: FilterTerm
  op: FilterOp // joins this term to everything before it (ignored on the first)
}

/**
 * Evaluate the token expression for one row, left-to-right. `matchText` tests a
 * text term against the row's columns. No tokens → everything matches.
 */
export function evalTokens(
  tokens: FilterToken[],
  matchText: (q: string) => boolean,
): boolean {
  let res: boolean | null = null
  for (const tok of tokens) {
    const m = matchText(tok.term.q)
    res = res === null ? m : tok.op === "and" ? res && m : res || m
  }
  return res ?? true
}

/** The active text terms of the expression (chips + the live input). */
export function textTerms(tokens: FilterToken[], search: string): string[] {
  const terms = tokens.map((t) => t.term.q)
  const live = search.trim()
  if (live) terms.push(live)
  return terms
}

/** Which of a row's fields contain any active text term (case-insensitive). */
export function matchedFields(
  fields: { key: string; value: string | null }[],
  terms: string[],
): Set<string> {
  const lowered = terms.map((t) => t.toLowerCase()).filter(Boolean)
  const set = new Set<string>()
  if (lowered.length === 0) return set
  for (const f of fields) {
    const val = (f.value ?? "").toLowerCase()
    if (lowered.some((t) => val.includes(t))) set.add(f.key)
  }
  return set
}

/** Renders text with any active search term wrapped in a yellow highlight
 *  span. Now lives in `../highlight` (shared with studio-v2's article list);
 *  re-exported here so the studio tables keep their import path. */
export { Highlight } from "../highlight"
